import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { 
  Bold, Italic, Underline as UnderlineIcon, 
  Heading1, Heading2, Heading3, 
  List, ListOrdered, 
  AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Undo, Redo, Pilcrow, Palette
} from 'lucide-react';
import { useEffect } from 'react';
import { stripHtml } from '@/lib/text';

const MenuButton = ({ onClick, isActive, children, title, style }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={style}
    className={`p-1.5 rounded hover:bg-[#3A3A3A] transition-colors ${
      isActive ? 'bg-[#A6C48A] text-[#010000]' : 'text-[#A1A1AA]'
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder, rows = 3 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[60px] px-3 py-2 text-[#F5F5F5] text-sm',
        style: `min-height: ${rows * 24}px`,
      },
    },
  });

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border border-[#3A3A3A] rounded bg-[#141414] overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-0.5 p-1.5 border-b border-[#3A3A3A] bg-[#1F1F1F]/50">
        {/* Text Style */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Negrito (Ctrl+B)"
        >
          <Bold size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Itálico (Ctrl+I)"
        >
          <Italic size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Sublinhado (Ctrl+U)"
        >
          <UnderlineIcon size={16} />
        </MenuButton>

        <div className="w-px bg-[#3A3A3A] mx-1" />

        {/* Headings */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Título H1"
        >
          <Heading1 size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Título H2"
        >
          <Heading2 size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Título H3"
        >
          <Heading3 size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          isActive={editor.isActive('paragraph')}
          title="Parágrafo"
        >
          <Pilcrow size={16} />
        </MenuButton>

        <div className="w-px bg-[#3A3A3A] mx-1" />

        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Lista"
        >
          <List size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Lista Numerada"
        >
          <ListOrdered size={16} />
        </MenuButton>

        <div className="w-px bg-[#3A3A3A] mx-1" />

        {/* Alignment */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Alinhar à Esquerda"
        >
          <AlignLeft size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Centrar"
        >
          <AlignCenter size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Alinhar à Direita"
        >
          <AlignRight size={16} />
        </MenuButton>

        <div className="w-px bg-[#3A3A3A] mx-1" />

        {/* Link */}
        <MenuButton
          onClick={addLink}
          isActive={editor.isActive('link')}
          title="Inserir Link"
        >
          <LinkIcon size={16} />
        </MenuButton>

        <div className="w-px bg-[#3A3A3A] mx-1" />

        {/* Color - Orange highlight */}
        <MenuButton
          onClick={() => editor.chain().focus().setColor('#A6C48A').run()}
          isActive={editor.isActive('textStyle', { color: '#A6C48A' })}
          title="Destaque verde"
          style={{ color: '#A6C48A' }}
        >
          <Palette size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().unsetColor().run()}
          isActive={false}
          title="Remover Cor"
        >
          <span className="text-xs font-bold">A</span>
        </MenuButton>

        <div className="w-px bg-[#3A3A3A] mx-1" />

        {/* Undo/Redo */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          isActive={false}
          title="Desfazer (Ctrl+Z)"
        >
          <Undo size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          isActive={false}
          title="Refazer (Ctrl+Y)"
        >
          <Redo size={16} />
        </MenuButton>
      </div>

      {/* Editor Content, com o texto de exemplo por cima quando está vazio */}
      <div className="relative">
        <EditorContent editor={editor} />
        {!stripHtml(value) && placeholder && (
          <div className="admin-placeholder absolute inset-x-0 top-0 px-3 py-2 text-sm pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}
